import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, FileTextIcon, InstagramIcon } from 'lucide-react';
const ASSETS = {
  bg: 'https://cdn.magicpatterns.com/uploads/13wPF2pwNks7fnUXcGvBXz/Banner.png',
  logo: 'https://cdn.magicpatterns.com/uploads/nEQZpv2cMq8qb6dzTnrAZs/Logo_sin_fondo.png',
  whatsapp: 'https://cdn.magicpatterns.com/uploads/61Z7DVa3GeRNvoqyHNE2wo/Logo_whatsapp_sin_fondo.png',
  facebook: 'https://cdn.magicpatterns.com/uploads/ebfHe82Jf4NxRk1sGmzt4E/Logo_facebook_sin_fondo.png',
  tiktok: 'https://cdn.magicpatterns.com/uploads/n3sGXWqDtHX5UB4h4tJCBj/Logo_tiktok.png'
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};
const buttonContainerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
const buttonVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.92
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 20
    }
  }
};
export function App() {
  return (
    // Outer container: Dark background for desktop, centers the mobile view
    <div className="min-h-screen bg-black flex justify-center items-center w-full font-sans selection:bg-krove selection:text-black" data-id="element-0">
      {/* Mobile container */}
      <div className="relative w-full max-w-[430px] min-h-screen flex flex-col items-center overflow-hidden shadow-2xl" data-id="element-1">
        {/* Background Image & Overlays */}
        <div className="absolute inset-0 z-0" data-id="element-2">
          <img src={ASSETS.bg} alt="Background" className="w-full h-full object-cover" data-id="element-3" />
          {/* Radial gradient overlay for depth: darker edges, lighter center */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.6)_100%)] pointer-events-none" data-id="element-4" />
        </div>

        {/* Main Content */}
        <motion.main className="relative z-10 flex flex-col items-center w-full min-h-screen py-12 px-6 justify-between" variants={containerVariants} initial="hidden" animate="show" data-id="element-5">
          {/* Top Section: Logo & Greeting */}
          <motion.div variants={itemVariants} className="flex flex-col items-center w-full mt-4 mb-8" data-id="element-6">
            <img src={ASSETS.logo} alt="Krove Latam Logo" className="w-[180px] h-auto drop-shadow-lg mb-6" data-id="element-7" />
            <p className="text-white/90 text-center text-[14px] font-medium leading-relaxed max-w-[300px] drop-shadow-md" data-id="element-8">
              Hola, somos Krove Latam. ¿Cómo podemos transformar tu negocio
              digital hoy?
            </p>
          </motion.div>

          {/* Middle Section: Links */}
          <motion.div className="flex flex-col w-full items-center gap-[14px] flex-1 justify-center mb-12" variants={buttonContainerVariants} initial="hidden" animate="show" data-id="element-9">
            {/* Button 1: Website */}
            <motion.a href="https://krove.lat/" target="_blank" rel="noopener noreferrer" variants={buttonVariants} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group relative w-full max-w-[340px] flex items-center justify-center p-[14px] px-6 rounded-xl overflow-hidden
                         backdrop-blur-[24px] bg-white/[0.12] border border-white/25
                         shadow-[0_8px_32px_0_rgba(0,0,0,0.3),inset_0_1px_0px_rgba(255,255,255,0.4),inset_0_-1px_0px_rgba(255,255,255,0.05)]
                         transition-all duration-300 ease-out
                         hover:bg-white/[0.18] hover:border-krove/80 hover:shadow-[0_0_24px_rgba(245,197,24,0.25),0_8px_32px_0_rgba(0,0,0,0.4),inset_0_1px_0px_rgba(255,255,255,0.5),inset_0_-1px_0px_rgba(255,255,255,0.1)]" data-id="element-10">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent pointer-events-none rounded-xl" data-id="element-11" />
              <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none" data-id="element-12" />
              <span className="relative z-10 text-white font-medium text-[15px] tracking-wide group-hover:text-krove transition-colors duration-300" data-id="element-13">
                Nuestra Website
              </span>
            </motion.a>

            {/* Button 2: WhatsApp */}
            <motion.a href="https://wa.me/573135250384?text=Hola%20Krove%20Latam%2C%20estoy%20interesad%40%20en%20tus%20servicios." target="_blank" rel="noopener noreferrer" variants={buttonVariants} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group relative w-full max-w-[340px] flex items-center p-[14px] px-6 rounded-xl overflow-hidden
                         backdrop-blur-[24px] bg-white/[0.12] border border-white/25
                         shadow-[0_8px_32px_0_rgba(0,0,0,0.3),inset_0_1px_0px_rgba(255,255,255,0.4),inset_0_-1px_0px_rgba(255,255,255,0.05)]
                         transition-all duration-300 ease-out
                         hover:bg-white/[0.18] hover:border-krove/80 hover:shadow-[0_0_24px_rgba(245,197,24,0.25),0_8px_32px_0_rgba(0,0,0,0.4),inset_0_1px_0px_rgba(255,255,255,0.5),inset_0_-1px_0px_rgba(255,255,255,0.1)]" data-id="element-14">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent pointer-events-none rounded-xl" data-id="element-15" />
              <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none" data-id="element-16" />
              <div className="relative z-10 flex items-center w-full" data-id="element-17">
                <div className="w-8 flex justify-start" data-id="element-18">
                  <img src={ASSETS.whatsapp} alt="WhatsApp" className="w-6 h-6 object-contain" data-id="element-19" />
                </div>
                <span className="flex-1 text-center pr-8 text-white font-medium text-[15px] group-hover:text-white transition-colors duration-300" data-id="element-20">
                  Escríbenos
                </span>
              </div>
            </motion.a>

            {/* Button 3: Email */}
            <motion.a href="mailto:krove.lat@gmail.com" variants={buttonVariants} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group relative w-full max-w-[340px] flex items-center p-[14px] px-6 rounded-xl overflow-hidden
                         backdrop-blur-[24px] bg-white/[0.12] border border-white/25
                         shadow-[0_8px_32px_0_rgba(0,0,0,0.3),inset_0_1px_0px_rgba(255,255,255,0.4),inset_0_-1px_0px_rgba(255,255,255,0.05)]
                         transition-all duration-300 ease-out
                         hover:bg-white/[0.18] hover:border-krove/80 hover:shadow-[0_0_24px_rgba(245,197,24,0.25),0_8px_32px_0_rgba(0,0,0,0.4),inset_0_1px_0px_rgba(255,255,255,0.5),inset_0_-1px_0px_rgba(255,255,255,0.1)]" data-id="element-21">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent pointer-events-none rounded-xl" data-id="element-22" />
              <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none" data-id="element-23" />
              <div className="relative z-10 flex items-center w-full" data-id="element-24">
                <div className="w-8 flex justify-start" data-id="element-25">
                  <MailIcon className="w-6 h-6 text-white group-hover:text-krove transition-colors duration-300" strokeWidth={1.5} data-id="element-26" />
                </div>
                <span className="flex-1 text-center pr-8 text-white font-medium text-[15px] transition-colors duration-300" data-id="element-27">
                  Email
                </span>
              </div>
            </motion.a>

            {/* Button 4: Regalo PDF */}
            <motion.a href="https://wa.me/573135250384?text=Quiero%20mi%20regalo" target="_blank" rel="noopener noreferrer" variants={buttonVariants} whileHover={{
              scale: 1.04
            }} whileTap={{
              scale: 0.97
            }} animate={{
              boxShadow: ['0 0 12px rgba(245,197,24,0.3), 0 8px 32px rgba(0,0,0,0.3)', '0 0 24px rgba(245,197,24,0.5), 0 8px 32px rgba(0,0,0,0.3)', '0 0 12px rgba(245,197,24,0.3), 0 8px 32px rgba(0,0,0,0.3)']
            }} transition={{
              boxShadow: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} className="group relative w-full max-w-[340px] flex items-center p-[14px] px-6 rounded-xl overflow-hidden
                         backdrop-blur-[24px] bg-krove/90 border border-krove
                         transition-all duration-300 ease-out
                         hover:bg-krove hover:shadow-[0_0_32px_rgba(245,197,24,0.5),0_8px_32px_0_rgba(0,0,0,0.4)]" data-id="element-28">
              <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/10 pointer-events-none rounded-xl" data-id="element-29" />
              <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none" data-id="element-30" />
              <div className="relative z-10 flex items-center w-full" data-id="element-31">
                <div className="w-8 flex justify-start" data-id="element-32">
                  <FileTextIcon className="w-6 h-6 text-black/80 group-hover:text-black transition-colors duration-300" strokeWidth={1.5} data-id="element-33" />
                </div>
                <span className="flex-1 text-center pr-8 text-black/90 font-semibold text-[15px] tracking-wide group-hover:text-black transition-colors duration-300" data-id="element-34">
                  Regalo PDF
                </span>
              </div>
            </motion.a>
          </motion.div>

          {/* Bottom Section: Footer */}
          <motion.div variants={itemVariants} className="flex flex-col items-center gap-4 mt-auto" data-id="element-35">
            <div className="flex flex-col items-center gap-3" data-id="element-36">
              <a href="#" className="flex items-center gap-2 text-white/80 hover:text-krove hover:drop-shadow-[0_0_8px_rgba(245,197,24,0.5)] transition-all duration-300" data-id="element-37">
                <InstagramIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-38" />
                <span className="text-[12px] font-medium tracking-wide" data-id="element-39">
                  @krove.lat
                </span>
              </a>
              <a href="#" className="flex items-center gap-2 text-white/80 hover:text-krove hover:drop-shadow-[0_0_8px_rgba(245,197,24,0.5)] transition-all duration-300" data-id="element-40">
                <img src={ASSETS.facebook} alt="Facebook" className="w-5 h-5 object-contain opacity-90" data-id="element-41" />
                <span className="text-[12px] font-medium tracking-wide" data-id="element-42">
                  Krove Latam
                </span>
              </a>
              <a href="#" className="flex items-center gap-2 text-white/80 hover:text-krove hover:drop-shadow-[0_0_8px_rgba(245,197,24,0.5)] transition-all duration-300" data-id="element-43">
                <img src={ASSETS.tiktok} alt="TikTok" className="w-5 h-5 object-contain opacity-90" data-id="element-44" />
                <span className="text-[12px] font-medium tracking-wide" data-id="element-45">
                  @krove.lat
                </span>
              </a>
            </div>
          </motion.div>
        </motion.main>
      </div>
    </div>
  );
}